import type { GtmOptions } from '@utilitywarehouse/tracking-adapter-gtm-web';
import type { SnowplowExtraContext } from '@utilitywarehouse/tracking-adapter-snowplow-web';
import type {
  Context,
  EventTuple,
  ExtraContext,
} from '@utilitywarehouse/tracking-core';
import { useTrackOnce, useTracking } from '@utilitywarehouse/tracking-react';

type EntityPayload<T> = ExtraContext<T> | SnowplowExtraContext<T>;

interface Options extends Record<string, any> {
  contexts?: Array<ExtraContext | SnowplowExtraContext>;
  gtm?: GtmOptions;
}

export interface SignupApplicationEntity {
  // ISO string timestamp
  created_at: string;
  // Basket id
  basket_id?: string;
  // Address supply postcode
  supply_postcode?: string;
  // Whether the prospect/customer is a home owner
  is_home_owner?: boolean;
  // The method used to convert the prospect into a customer
  sales_method?: string;
  // Describes where the prospect originated from
  origin?: string;
  // Categorizes the type of referrer
  referrer_type?: string;
  // Provides additional metadata about the referrer
  referrer_details?: string;
  // Categorizes the type of assistant
  assistant_type?: string;
  // Provides additional metadata about the assistant
  assistant_details?: string;
  // The logged in partner ID
  partner_id?: string;
  // The logged in partner position ID
  partner_position_id?: string;
  // The type of appointment, remote or in-person
  session_type: string;
  // is partner session assisted by supporting partner
  is_supported?: boolean;
  // The supporting partner position ID
  supporting_partner_position_id?: string;
  // is partner session via hosted link
  is_hosted_link?: boolean;
  // Whether boiler home cover is available for selection
  is_boiler_home_cover_available?: boolean;
  // Whether income protector is available for selection
  is_income_protector_available?: boolean;
  // Who is the broadband provisioning supplier
  broadband_provisioning_supplier?: string;
  is_property_already_supplied?: boolean;
}

// Signup event meta entity. Data points applied to every event.
export function signupApplicationEntity(
  context: SignupApplicationEntity,
  options?: Options
): EntityPayload<SignupApplicationEntity> {
  return {
    ...options,
    name: 'signup_application',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.signup/signup_application/jsonschema/1-1-2',
    },
  };
}

// User is shown the credit check paragraph during checkout.
// example usage: tracker.track(trackSignupCreditCheckNoticeDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupCreditCheckNoticeDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_credit_check_notice_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_credit_check_notice_displayed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupCreditCheckNoticeDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCreditCheckNoticeDisplayedEvent(context, options));
}

export function useTrackSignupCreditCheckNoticeDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCreditCheckNoticeDisplayedEvent(context, options));
}

export interface SignupContractExpiryDateSubmittedEventContext {
  // The name/identifier of the expiring product
  product: string;
}

// User submits the expiry date for an unselected product.
// example usage: tracker.track(trackSignupContractExpiryDateSubmitted({prop: 'value'}, {option1: 'value'}));
export function signupContractExpiryDateSubmittedEvent(
  context: SignupContractExpiryDateSubmittedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_contract_expiry_date_submitted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_contract_expiry_date_submitted/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupContractExpiryDateSubmitted() {
  const { track } = useTracking();

  return (
    context: SignupContractExpiryDateSubmittedEventContext,
    options?: Options
  ) => track(signupContractExpiryDateSubmittedEvent(context, options));
}

export function useTrackSignupContractExpiryDateSubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupContractExpiryDateSubmittedEventContext,
    options?: Options
  ) => trackOnce(signupContractExpiryDateSubmittedEvent(context, options));
}

export interface SignupAddressAlreadySuppliedEventContext {
  // Organisation registered at this address
  organisation?: string;
  // Department. Organisation is traditionally required if this is present
  department?: string;
  // Used where a building number and / or building name exists and is subdivided
  sub_building?: string;
  // Name of residential or commercial premise
  building_name?: string;
  // Number to identify building on a street or sub-street
  building_number?: string;
  // Suplement street name
  dependent_thoroughfare?: string;
  // Street name
  thoroughfare?: string;
  // Very small villages or hamlets, occasionally business parks
  double_dependent_locality?: string;
  // A small town or village name sometimes included in an address
  dependent_locality?: string;
  // In some small towns may be a larger, nearby town
  post_town?: string;
  postcode: string;
  // Unique Property Reference Number
  uprn?: string;
}

// User entered an address that is already supplied.
// example usage: tracker.track(trackSignupAddressAlreadySupplied({prop: 'value'}, {option1: 'value'}));
export function signupAddressAlreadySuppliedEvent(
  context: SignupAddressAlreadySuppliedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_address_already_supplied',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_address_already_supplied/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupAddressAlreadySupplied() {
  const { track } = useTracking();

  return (
    context: SignupAddressAlreadySuppliedEventContext,
    options?: Options
  ) => track(signupAddressAlreadySuppliedEvent(context, options));
}

export function useTrackSignupAddressAlreadySuppliedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupAddressAlreadySuppliedEventContext,
    options?: Options
  ) => trackOnce(signupAddressAlreadySuppliedEvent(context, options));
}

export interface SignupMobileSelectionsSubmittedForOneSimEventContext {
  // The number to represent the service added (4 is the highest)
  service_number: number;
  // True if the user opts to keep the existing mobile number for the SIM
  keep_existing_mobile_number: boolean;
  // True if the user opts to cancel a service from their existing supplier, when setting up the new SIM
  cancel_existing_service: boolean;
  // True if the user opts to set budget control for the SIM
  set_budget_control: boolean;
}

// User submits selections for the setup of one SIM (e.g. whether to keepexisting number). The service_number represents which SIM the usersubmitted their selections for, from the order they appear on the page.
// example usage: tracker.track(trackSignupMobileSelectionsSubmittedForOneSim({prop: 'value'}, {option1: 'value'}));
export function signupMobileSelectionsSubmittedForOneSimEvent(
  context: SignupMobileSelectionsSubmittedForOneSimEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_mobile_selections_submitted_for_one_sim',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_mobile_selections_submitted_for_one_sim/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupMobileSelectionsSubmittedForOneSim() {
  const { track } = useTracking();

  return (
    context: SignupMobileSelectionsSubmittedForOneSimEventContext,
    options?: Options
  ) => track(signupMobileSelectionsSubmittedForOneSimEvent(context, options));
}

export function useTrackSignupMobileSelectionsSubmittedForOneSimOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupMobileSelectionsSubmittedForOneSimEventContext,
    options?: Options
  ) =>
    trackOnce(signupMobileSelectionsSubmittedForOneSimEvent(context, options));
}

export interface SignupBroadbandDependencyQuestionAnsweredEventContext {
  // Number representing which of the 2 questions in the sequence was answered
  question_number: number;
  // User answered "yes" to the dependency question (if any question was answered yes in this journey, we block the order)
  answered_yes: boolean;
}

// User answers the digital landline dependency questions.
// example usage: tracker.track(trackSignupBroadbandDependencyQuestionAnswered({prop: 'value'}, {option1: 'value'}));
export function signupBroadbandDependencyQuestionAnsweredEvent(
  context: SignupBroadbandDependencyQuestionAnsweredEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_broadband_dependency_question_answered',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_broadband_dependency_question_answered/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupBroadbandDependencyQuestionAnswered() {
  const { track } = useTracking();

  return (
    context: SignupBroadbandDependencyQuestionAnsweredEventContext,
    options?: Options
  ) => track(signupBroadbandDependencyQuestionAnsweredEvent(context, options));
}

export function useTrackSignupBroadbandDependencyQuestionAnsweredOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupBroadbandDependencyQuestionAnsweredEventContext,
    options?: Options
  ) =>
    trackOnce(signupBroadbandDependencyQuestionAnsweredEvent(context, options));
}

// User expanded the energy tariff breakdown panel.
// example usage: tracker.track(trackSignupEnergyTariffBreakdownExpanded({prop: 'value'}, {option1: 'value'}));
export function signupEnergyTariffBreakdownExpandedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_energy_tariff_breakdown_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_energy_tariff_breakdown_expanded/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupEnergyTariffBreakdownExpanded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupEnergyTariffBreakdownExpandedEvent(context, options));
}

export function useTrackSignupEnergyTariffBreakdownExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupEnergyTariffBreakdownExpandedEvent(context, options));
}

export interface SignupEnergyFaqExpandedEventContext {
  // The question that was expanded
  question: string;
}

// User has expanded a question from the signup journey energy FAQs.
// example usage: tracker.track(trackSignupEnergyFaqExpanded({prop: 'value'}, {option1: 'value'}));
export function signupEnergyFaqExpandedEvent(
  context: SignupEnergyFaqExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_energy_faq_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_energy_faq_expanded/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupEnergyFaqExpanded() {
  const { track } = useTracking();

  return (context: SignupEnergyFaqExpandedEventContext, options?: Options) =>
    track(signupEnergyFaqExpandedEvent(context, options));
}

export function useTrackSignupEnergyFaqExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupEnergyFaqExpandedEventContext, options?: Options) =>
    trackOnce(signupEnergyFaqExpandedEvent(context, options));
}

// User selected "Yes" when asked "Did you move into your home within the last six months?" during checkout.
// example usage: tracker.track(trackSignupRecentMoveSelected({prop: 'value'}, {option1: 'value'}));
export function signupRecentMoveSelectedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_recent_move_selected',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_recent_move_selected/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupRecentMoveSelected() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupRecentMoveSelectedEvent(context, options));
}

export function useTrackSignupRecentMoveSelectedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupRecentMoveSelectedEvent(context, options));
}

// User opened the dropdown to edit energy details, changed something and clicked 'Save details'.
// example usage: tracker.track(trackSignupEnergyDetailsEdited({prop: 'value'}, {option1: 'value'}));
export function signupEnergyDetailsEditedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_energy_details_edited',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_energy_details_edited/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupEnergyDetailsEdited() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupEnergyDetailsEditedEvent(context, options));
}

export function useTrackSignupEnergyDetailsEditedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupEnergyDetailsEditedEvent(context, options));
}

// Basket panel is displayed to the user.
// example usage: tracker.track(trackSignupBasketPanelDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupBasketPanelDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_basket_panel_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_basket_panel_displayed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupBasketPanelDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBasketPanelDisplayedEvent(context, options));
}

export function useTrackSignupBasketPanelDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBasketPanelDisplayedEvent(context, options));
}

// User has scrolled far enough to have the Thank You page "Give us a call" header displayed in their viewport.
// example usage: tracker.track(trackSignupThankyouCtaDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupThankyouCtaDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_thankyou_cta_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_thankyou_cta_displayed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupThankyouCtaDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupThankyouCtaDisplayedEvent(context, options));
}

export function useTrackSignupThankyouCtaDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupThankyouCtaDisplayedEvent(context, options));
}

export interface SignupIpWhatsNotCoveredClickedEventContext {
  // The policy feature that is clicked e.g. "Personal accident cover"
  policy_feature: string;
}

// In the Income Protector (IP) sign-up journey, the user clicks the tabto display what's "Not covered" (within the "What's covered?" section)
// example usage: tracker.track(trackSignupIpWhatsNotCoveredClicked({prop: 'value'}, {option1: 'value'}));
export function signupIpWhatsNotCoveredClickedEvent(
  context: SignupIpWhatsNotCoveredClickedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_ip_whats_not_covered_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ip_whats_not_covered_clicked/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupIpWhatsNotCoveredClicked() {
  const { track } = useTracking();

  return (
    context: SignupIpWhatsNotCoveredClickedEventContext,
    options?: Options
  ) => track(signupIpWhatsNotCoveredClickedEvent(context, options));
}

export function useTrackSignupIpWhatsNotCoveredClickedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupIpWhatsNotCoveredClickedEventContext,
    options?: Options
  ) => trackOnce(signupIpWhatsNotCoveredClickedEvent(context, options));
}

export interface SignupDepositConfirmedEventContext {
  // Deposit amount in pence
  amount: number;
}

// User clicks "Pay and confirm" to pay a deposit during the sign-up journey.
// example usage: tracker.track(trackSignupDepositConfirmed({prop: 'value'}, {option1: 'value'}));
export function signupDepositConfirmedEvent(
  context: SignupDepositConfirmedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_deposit_confirmed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_deposit_confirmed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupDepositConfirmed() {
  const { track } = useTracking();

  return (context: SignupDepositConfirmedEventContext, options?: Options) =>
    track(signupDepositConfirmedEvent(context, options));
}

export function useTrackSignupDepositConfirmedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupDepositConfirmedEventContext, options?: Options) =>
    trackOnce(signupDepositConfirmedEvent(context, options));
}

// User has clicked the "Share your link" button to share the refer a friend URL.
// example usage: tracker.track(trackSignupReferAFriendBannerShareUrlClicked({prop: 'value'}, {option1: 'value'}));
export function signupReferAFriendBannerShareUrlClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_refer_a_friend_banner_share_url_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_refer_a_friend_banner_share_url_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupReferAFriendBannerShareUrlClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupReferAFriendBannerShareUrlClickedEvent(context, options));
}

export function useTrackSignupReferAFriendBannerShareUrlClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupReferAFriendBannerShareUrlClickedEvent(context, options));
}

// User has clicked the "Copy your link" button to copy the refer a friend URL.
// example usage: tracker.track(trackSignupReferAFriendBannerCopyUrlClicked({prop: 'value'}, {option1: 'value'}));
export function signupReferAFriendBannerCopyUrlClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_refer_a_friend_banner_copy_url_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_refer_a_friend_banner_copy_url_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupReferAFriendBannerCopyUrlClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupReferAFriendBannerCopyUrlClickedEvent(context, options));
}

export function useTrackSignupReferAFriendBannerCopyUrlClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupReferAFriendBannerCopyUrlClickedEvent(context, options));
}

// The refer a friend banner has been displayed to the user.
// example usage: tracker.track(trackSignupReferAFriendBannerDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupReferAFriendBannerDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_refer_a_friend_banner_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_refer_a_friend_banner_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupReferAFriendBannerDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupReferAFriendBannerDisplayedEvent(context, options));
}

export function useTrackSignupReferAFriendBannerDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupReferAFriendBannerDisplayedEvent(context, options));
}

export interface SignupBroadbandAddedToBasketEventContext {
  // Tariff selected by the user (e.g. Full Fibre 500)
  tariff: string;
  // User chooses whole home WiFi for their broadband
  whole_home_wifi: boolean;
  // User provides a landline number
  landline_provided: boolean;
  // The call package selected by the user
  call_package?: string;
}

// User completes the Broadband page of the sign-up journey, so broadband isadded to their basket
// example usage: tracker.track(trackSignupBroadbandAddedToBasket({prop: 'value'}, {option1: 'value'}));
export function signupBroadbandAddedToBasketEvent(
  context: SignupBroadbandAddedToBasketEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_broadband_added_to_basket',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_broadband_added_to_basket/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupBroadbandAddedToBasket() {
  const { track } = useTracking();

  return (
    context: SignupBroadbandAddedToBasketEventContext,
    options?: Options
  ) => track(signupBroadbandAddedToBasketEvent(context, options));
}

export function useTrackSignupBroadbandAddedToBasketOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupBroadbandAddedToBasketEventContext,
    options?: Options
  ) => trackOnce(signupBroadbandAddedToBasketEvent(context, options));
}

export interface SignupIpDocumentClickedEventContext {
  // The title of the document opened by the user (e.g. IPID, Terms ofBusiness, Policy Wording, etc.)
  document_title: string;
}

// In the Income Protector (IP) sign-up journey, the user clicks alink to one of the policy documents
// example usage: tracker.track(trackSignupIpDocumentClicked({prop: 'value'}, {option1: 'value'}));
export function signupIpDocumentClickedEvent(
  context: SignupIpDocumentClickedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_ip_document_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ip_document_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupIpDocumentClicked() {
  const { track } = useTracking();

  return (context: SignupIpDocumentClickedEventContext, options?: Options) =>
    track(signupIpDocumentClickedEvent(context, options));
}

export function useTrackSignupIpDocumentClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupIpDocumentClickedEventContext, options?: Options) =>
    trackOnce(signupIpDocumentClickedEvent(context, options));
}

export interface SignupIpWhatsCoveredFeatureExpandedEventContext {
  // The feature that is expanded e.g. "Personal accident cover"
  feature: string;
}

// In the Income Protector (IP) sign-up journey, the user expands aspecific feature under the "Covered" tab within the "What's covered?"section
// example usage: tracker.track(trackSignupIpWhatsCoveredFeatureExpanded({prop: 'value'}, {option1: 'value'}));
export function signupIpWhatsCoveredFeatureExpandedEvent(
  context: SignupIpWhatsCoveredFeatureExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_ip_whats_covered_feature_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ip_whats_covered_feature_expanded/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupIpWhatsCoveredFeatureExpanded() {
  const { track } = useTracking();

  return (
    context: SignupIpWhatsCoveredFeatureExpandedEventContext,
    options?: Options
  ) => track(signupIpWhatsCoveredFeatureExpandedEvent(context, options));
}

export function useTrackSignupIpWhatsCoveredFeatureExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupIpWhatsCoveredFeatureExpandedEventContext,
    options?: Options
  ) => trackOnce(signupIpWhatsCoveredFeatureExpandedEvent(context, options));
}

// In the Income Protector (IP) sign-up journey, the user has scrolledfar enough to have the BHC FAQs displayed in their viewport.
// example usage: tracker.track(trackSignupIpFaqsDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupIpFaqsDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_ip_faqs_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ip_faqs_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupIpFaqsDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupIpFaqsDisplayedEvent(context, options));
}

export function useTrackSignupIpFaqsDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupIpFaqsDisplayedEvent(context, options));
}

// User has scrolled far enough to have the the energy FAQs header displayed in their viewport.
// example usage: tracker.track(trackSignupEnergyFaqsHeaderDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupEnergyFaqsHeaderDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_energy_faqs_header_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_energy_faqs_header_displayed/jsonschema/1-0-5',
      },
    },
  ];
}

export function useTrackSignupEnergyFaqsHeaderDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupEnergyFaqsHeaderDisplayedEvent(context, options));
}

export function useTrackSignupEnergyFaqsHeaderDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupEnergyFaqsHeaderDisplayedEvent(context, options));
}

export interface SignupIpFaqExpandedEventContext {
  // The question that was expanded
  question: string;
}

// User expands a question from the Income Protector (IP) FAQs, duringthe sign-up journey.
// example usage: tracker.track(trackSignupIpFaqExpanded({prop: 'value'}, {option1: 'value'}));
export function signupIpFaqExpandedEvent(
  context: SignupIpFaqExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_ip_faq_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ip_faq_expanded/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupIpFaqExpanded() {
  const { track } = useTracking();

  return (context: SignupIpFaqExpandedEventContext, options?: Options) =>
    track(signupIpFaqExpandedEvent(context, options));
}

export function useTrackSignupIpFaqExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupIpFaqExpandedEventContext, options?: Options) =>
    trackOnce(signupIpFaqExpandedEvent(context, options));
}

// User adds an Income Protector (IP) service to their basket.
// example usage: tracker.track(trackSignupIpAddedToBasket({prop: 'value'}, {option1: 'value'}));
export function signupIpAddedToBasketEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_ip_added_to_basket',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ip_added_to_basket/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupIpAddedToBasket() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupIpAddedToBasketEvent(context, options));
}

export function useTrackSignupIpAddedToBasketOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupIpAddedToBasketEvent(context, options));
}

// User has scrolled far enough to have the the cashback card FAQs displayed in their viewport.
// example usage: tracker.track(trackSignupCbcFaqsDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupCbcFaqsDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_faqs_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_faqs_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcFaqsDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCbcFaqsDisplayedEvent(context, options));
}

export function useTrackSignupCbcFaqsDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCbcFaqsDisplayedEvent(context, options));
}

// User has scrolled far enough to have the the retail partner carousel displayed in their viewport on the cashback card page in the sign-up journey.
// example usage: tracker.track(trackSignupCbcRetailPartnerCarouselDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupCbcRetailPartnerCarouselDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_retail_partner_carousel_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_retail_partner_carousel_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcRetailPartnerCarouselDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCbcRetailPartnerCarouselDisplayedEvent(context, options));
}

export function useTrackSignupCbcRetailPartnerCarouselDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCbcRetailPartnerCarouselDisplayedEvent(context, options));
}

// User has scrolled far enough to have the the review carousel displayed in their viewport on the cashback card page in the sign-up journey.
// example usage: tracker.track(trackSignupCbcReviewCarouselDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupCbcReviewCarouselDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_review_carousel_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_review_carousel_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcReviewCarouselDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCbcReviewCarouselDisplayedEvent(context, options));
}

export function useTrackSignupCbcReviewCarouselDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCbcReviewCarouselDisplayedEvent(context, options));
}

// User has interacted with a navigation element from the retail partner carousel on the cashback card section of the sign-up journey.
// example usage: tracker.track(trackSignupCbcRetailPartnerCarouselNavButtonClicked({prop: 'value'}, {option1: 'value'}));
export function signupCbcRetailPartnerCarouselNavButtonClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_retail_partner_carousel_nav_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_retail_partner_carousel_nav_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcRetailPartnerCarouselNavButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(
      signupCbcRetailPartnerCarouselNavButtonClickedEvent(context, options)
    );
}

export function useTrackSignupCbcRetailPartnerCarouselNavButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(
      signupCbcRetailPartnerCarouselNavButtonClickedEvent(context, options)
    );
}

// User has interacted with a navigation element from the review carousel on the cashback card section of the sign-up journey.
// example usage: tracker.track(trackSignupCbcReviewCarouselNavButtonClicked({prop: 'value'}, {option1: 'value'}));
export function signupCbcReviewCarouselNavButtonClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_review_carousel_nav_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_review_carousel_nav_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcReviewCarouselNavButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCbcReviewCarouselNavButtonClickedEvent(context, options));
}

export function useTrackSignupCbcReviewCarouselNavButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCbcReviewCarouselNavButtonClickedEvent(context, options));
}

export interface SignupCbcFaqExpandedEventContext {
  // The question that was expanded
  question: string;
}

// User has expanded a question from the signup journey cashback card FAQs.
// example usage: tracker.track(trackSignupCbcFaqExpanded({prop: 'value'}, {option1: 'value'}));
export function signupCbcFaqExpandedEvent(
  context: SignupCbcFaqExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_faq_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_faq_expanded/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcFaqExpanded() {
  const { track } = useTracking();

  return (context: SignupCbcFaqExpandedEventContext, options?: Options) =>
    track(signupCbcFaqExpandedEvent(context, options));
}

export function useTrackSignupCbcFaqExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupCbcFaqExpandedEventContext, options?: Options) =>
    trackOnce(signupCbcFaqExpandedEvent(context, options));
}

// User expanded the basket accordion on a small device.
// example usage: tracker.track(trackSignupBasketPanelExpanded({prop: 'value'}, {option1: 'value'}));
export function signupBasketPanelExpandedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_basket_panel_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_basket_panel_expanded/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupBasketPanelExpanded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBasketPanelExpandedEvent(context, options));
}

export function useTrackSignupBasketPanelExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBasketPanelExpandedEvent(context, options));
}

// User has requested control during a remote appointment
// example usage: tracker.track(trackSignupRemoteControlRequested({prop: 'value'}, {option1: 'value'}));
export function signupRemoteControlRequestedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_remote_control_requested',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_remote_control_requested/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupRemoteControlRequested() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupRemoteControlRequestedEvent(context, options));
}

export function useTrackSignupRemoteControlRequestedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupRemoteControlRequestedEvent(context, options));
}

// Control was taken from the currently controlling participant
// example usage: tracker.track(trackSignupRemoteControlTaken({prop: 'value'}, {option1: 'value'}));
export function signupRemoteControlTakenEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_remote_control_taken',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_remote_control_taken/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupRemoteControlTaken() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupRemoteControlTakenEvent(context, options));
}

export function useTrackSignupRemoteControlTakenOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupRemoteControlTakenEvent(context, options));
}

// User had their control request denied by the presently controlling participant
// example usage: tracker.track(trackSignupRemoteControlRequestDenied({prop: 'value'}, {option1: 'value'}));
export function signupRemoteControlRequestDeniedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_remote_control_request_denied',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_remote_control_request_denied/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupRemoteControlRequestDenied() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupRemoteControlRequestDeniedEvent(context, options));
}

export function useTrackSignupRemoteControlRequestDeniedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupRemoteControlRequestDeniedEvent(context, options));
}

// User joins a remote appointment
// example usage: tracker.track(trackSignupRemoteAppointmentJoined({prop: 'value'}, {option1: 'value'}));
export function signupRemoteAppointmentJoinedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_remote_appointment_joined',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_remote_appointment_joined/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupRemoteAppointmentJoined() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupRemoteAppointmentJoinedEvent(context, options));
}

export function useTrackSignupRemoteAppointmentJoinedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupRemoteAppointmentJoinedEvent(context, options));
}

// User joins a remote appointment
// example usage: tracker.track(trackSignupRemoteAppointmentEnded({prop: 'value'}, {option1: 'value'}));
export function signupRemoteAppointmentEndedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_remote_appointment_ended',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_remote_appointment_ended/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupRemoteAppointmentEnded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupRemoteAppointmentEndedEvent(context, options));
}

export function useTrackSignupRemoteAppointmentEndedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupRemoteAppointmentEndedEvent(context, options));
}

export interface SignupRemoteAppointmentEntity {
  // Who is currently in control
  control: string;
  // Current actor in the context of the remote application
  remote_actor: string;
  // ID of the current remote appointment
  appointment_id: string;
}

// Signup remote appointment meta entity. Data points applied to remote session interaction events.
export function signupRemoteAppointmentEntity(
  context: SignupRemoteAppointmentEntity,
  options?: Options
): EntityPayload<SignupRemoteAppointmentEntity> {
  return {
    ...options,
    name: 'signup_remote_appointment',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.signup/signup_remote_appointment/jsonschema/1-0-1',
    },
  };
}

// User had their control request accepted by the current controlling participant
// example usage: tracker.track(trackSignupRemoteControlAccepted({prop: 'value'}, {option1: 'value'}));
export function signupRemoteControlAcceptedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_remote_control_accepted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_remote_control_accepted/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupRemoteControlAccepted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupRemoteControlAcceptedEvent(context, options));
}

export function useTrackSignupRemoteControlAcceptedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupRemoteControlAcceptedEvent(context, options));
}

// In the Energy sign-up journey, user has scrolled far enoughto have the EV banner displayed in their viewport.
// example usage: tracker.track(trackSignupEvBannerDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupEvBannerDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_ev_banner_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ev_banner_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupEvBannerDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupEvBannerDisplayedEvent(context, options));
}

export function useTrackSignupEvBannerDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupEvBannerDisplayedEvent(context, options));
}

// In the Energy sign-up journey, the user clicks alink to see full EV tariff details
// example usage: tracker.track(trackSignupEvLinkClicked({prop: 'value'}, {option1: 'value'}));
export function signupEvLinkClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_ev_link_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ev_link_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupEvLinkClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupEvLinkClickedEvent(context, options));
}

export function useTrackSignupEvLinkClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupEvLinkClickedEvent(context, options));
}

export interface SignupCheckoutEntity {
  // Sum total price of all selected products in pennies
  actual_monthly_cost: number;
  // User adds a joint account holder on the checkout page as part of their application submission, having added the detail in the form
  joint_account_holder_added: boolean;
  // User adds an additional cbc holder on the checkout page as part of their application submission, having added the detail in the form
  cbc_holder_added?: boolean;
  // User selects 'Yes' for 'Did you move into this address within the last six months?' on the checkout page
  recent_move: boolean;
  // User added either a customer or partner as a referrer, if set
  referrer_type?: string;
  // The ID of the referrer, if set
  referrer_id?: string;
  // User selects 'Yes' for 'Are you currently paying for a supply at <pre-populated address> that you would like to switch to UW' on /order/confirm page
  existing_broadband_line?: boolean;
  // User clicked 'My provider isn't listed' checkbox in Your broadband switch section on /order/confirm page
  existing_broadband_provider_not_listed?: boolean;
  // The old provider shortcode provided for OTS (one touch switch)
  existing_broadband_provider_shortcode?: string;
  // User checks checkbox for 'Email me with more information about your Electric Vehicle Tariff' on the Energy page
  ev_expression_of_interest: boolean;
  // The chosen energy usage category
  energy_usage_category: string;
}

// Information about the signup completion process
export function signupCheckoutEntity(
  context: SignupCheckoutEntity,
  options?: Options
): EntityPayload<SignupCheckoutEntity> {
  return {
    ...options,
    name: 'signup_checkout',
    data: context,
    snowplow: {
      schema: 'iglu:uk.co.uw.quoting.signup/signup_checkout/jsonschema/1-0-7',
    },
  };
}

export interface SignupMobileServicesEntity {
  // The tariff(s) that the user has selected for their SIM(s)
  tariffs: string[];
  // The number of mobile services where the user wants to keep their mobile number
  count_mobile_numbers_to_keep: number;
  // The number of mobile services where the user wants to cancel a service with an existing supplier
  count_existing_services_to_cancel: number;
  // The number of mobile services where the user wants to set budget control
  count_services_for_budget_control: number;
}

// Detail on the mobile services selected during the sign-up journey.
export function signupMobileServicesEntity(
  context: SignupMobileServicesEntity,
  options?: Options
): EntityPayload<SignupMobileServicesEntity> {
  return {
    ...options,
    name: 'signup_mobile_services',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.signup/signup_mobile_services/jsonschema/1-0-1',
    },
  };
}

export interface SignupOnequoteRestoreContinuedEventContext {
  // Whether the postcode check was completed successfully
  postcode_check_success: boolean;
}

// User has attempted to restore their quote
// example usage: tracker.track(trackSignupOnequoteRestoreContinued({prop: 'value'}, {option1: 'value'}));
export function signupOnequoteRestoreContinuedEvent(
  context: SignupOnequoteRestoreContinuedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_onequote_restore_continued',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_onequote_restore_continued/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupOnequoteRestoreContinued() {
  const { track } = useTracking();

  return (
    context: SignupOnequoteRestoreContinuedEventContext,
    options?: Options
  ) => track(signupOnequoteRestoreContinuedEvent(context, options));
}

export function useTrackSignupOnequoteRestoreContinuedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupOnequoteRestoreContinuedEventContext,
    options?: Options
  ) => trackOnce(signupOnequoteRestoreContinuedEvent(context, options));
}

// In the Boiler & Home Cover (BHC) sign-up journey, the user clicks to play an explainer video
// example usage: tracker.track(trackSignupBhcVideoPlayed({prop: 'value'}, {option1: 'value'}));
export function signupBhcVideoPlayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_bhc_video_played',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bhc_video_played/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupBhcVideoPlayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBhcVideoPlayedEvent(context, options));
}

export function useTrackSignupBhcVideoPlayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBhcVideoPlayedEvent(context, options));
}

export interface SignupStartPartnerAppointmentClickedEventContext {
  // The appointment option selected by the Partner.Possible values:- face_to_face- partner_remote- support_remote- getting_quote
  appointment_type: string;
}

// The partner submits the appointment setup form on the /partner page to start a Partner appointment
// example usage: tracker.track(trackSignupStartPartnerAppointmentClicked({prop: 'value'}, {option1: 'value'}));
export function signupStartPartnerAppointmentClickedEvent(
  context: SignupStartPartnerAppointmentClickedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_start_partner_appointment_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_start_partner_appointment_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupStartPartnerAppointmentClicked() {
  const { track } = useTracking();

  return (
    context: SignupStartPartnerAppointmentClickedEventContext,
    options?: Options
  ) => track(signupStartPartnerAppointmentClickedEvent(context, options));
}

export function useTrackSignupStartPartnerAppointmentClickedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupStartPartnerAppointmentClickedEventContext,
    options?: Options
  ) => trackOnce(signupStartPartnerAppointmentClickedEvent(context, options));
}

export interface SignupOnequoteSaveSubmittedEventContext {
  // Whether the user has entered an email to send the quote to
  email_entered: boolean;
  // Whether the user has entered a phone number to send the quote to
  mobile_entered: boolean;
}

// Via the Save and Share (OneQuote) feature, the user has submitted the save form andsaved their quote.
// example usage: tracker.track(trackSignupOnequoteSaveSubmitted({prop: 'value'}, {option1: 'value'}));
export function signupOnequoteSaveSubmittedEvent(
  context: SignupOnequoteSaveSubmittedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_onequote_save_submitted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_onequote_save_submitted/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupOnequoteSaveSubmitted() {
  const { track } = useTracking();

  return (
    context: SignupOnequoteSaveSubmittedEventContext,
    options?: Options
  ) => track(signupOnequoteSaveSubmittedEvent(context, options));
}

export function useTrackSignupOnequoteSaveSubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupOnequoteSaveSubmittedEventContext,
    options?: Options
  ) => trackOnce(signupOnequoteSaveSubmittedEvent(context, options));
}

export interface SignupContractExpiryDatePromoDisplayedEventContext {
  // The name/identifier of the expiring product
  product: string;
}

// User has scrolled far enough to have the promo header for contract expirydate capture display in their viewport, for the specific product.
// example usage: tracker.track(trackSignupContractExpiryDatePromoDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupContractExpiryDatePromoDisplayedEvent(
  context: SignupContractExpiryDatePromoDisplayedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_contract_expiry_date_promo_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_contract_expiry_date_promo_displayed/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupContractExpiryDatePromoDisplayed() {
  const { track } = useTracking();

  return (
    context: SignupContractExpiryDatePromoDisplayedEventContext,
    options?: Options
  ) => track(signupContractExpiryDatePromoDisplayedEvent(context, options));
}

export function useTrackSignupContractExpiryDatePromoDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupContractExpiryDatePromoDisplayedEventContext,
    options?: Options
  ) => trackOnce(signupContractExpiryDatePromoDisplayedEvent(context, options));
}

// User has scrolled far enough to have the the broadband FAQs displayed in their viewport.
// example usage: tracker.track(trackSignupBroadbandFaqsDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupBroadbandFaqsDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_broadband_faqs_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_broadband_faqs_displayed/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupBroadbandFaqsDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBroadbandFaqsDisplayedEvent(context, options));
}

export function useTrackSignupBroadbandFaqsDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBroadbandFaqsDisplayedEvent(context, options));
}

export interface SignupBroadbandPageSubmittedEventContext {
  // tariff type the user selecte (e.g. Full Fibre 500)
  tariff: string;
  // Do people choose whole whome wifi
  whole_home_wifi: boolean;
  // Does user provide a landline number
  landline_provided: boolean;
  // Which call package did user take
  call_package?: string;
}

// When user submits 'add to bundle', details which they selected.
// example usage: tracker.track(trackSignupBroadbandPageSubmitted({prop: 'value'}, {option1: 'value'}));
export function signupBroadbandPageSubmittedEvent(
  context: SignupBroadbandPageSubmittedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_broadband_page_submitted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_broadband_page_submitted/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupBroadbandPageSubmitted() {
  const { track } = useTracking();

  return (
    context: SignupBroadbandPageSubmittedEventContext,
    options?: Options
  ) => track(signupBroadbandPageSubmittedEvent(context, options));
}

export function useTrackSignupBroadbandPageSubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupBroadbandPageSubmittedEventContext,
    options?: Options
  ) => trackOnce(signupBroadbandPageSubmittedEvent(context, options));
}

// User views the suggested broadband appointment card in the signup journey.
// example usage: tracker.track(trackSignupSuggestedBroadbandAppointmentCardViewed({prop: 'value'}, {option1: 'value'}));
export function signupSuggestedBroadbandAppointmentCardViewedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_suggested_broadband_appointment_card_viewed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_suggested_broadband_appointment_card_viewed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupSuggestedBroadbandAppointmentCardViewed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupSuggestedBroadbandAppointmentCardViewedEvent(context, options));
}

export function useTrackSignupSuggestedBroadbandAppointmentCardViewedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(
      signupSuggestedBroadbandAppointmentCardViewedEvent(context, options)
    );
}

// User clicks the change appointment button on the suggested broadband appointment card in the signup journey.
// example usage: tracker.track(trackSignupSuggestedBroadbandAppointmentChangeButtonClicked({prop: 'value'}, {option1: 'value'}));
export function signupSuggestedBroadbandAppointmentChangeButtonClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_suggested_broadband_appointment_change_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_suggested_broadband_appointment_change_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupSuggestedBroadbandAppointmentChangeButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(
      signupSuggestedBroadbandAppointmentChangeButtonClickedEvent(
        context,
        options
      )
    );
}

export function useTrackSignupSuggestedBroadbandAppointmentChangeButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(
      signupSuggestedBroadbandAppointmentChangeButtonClickedEvent(
        context,
        options
      )
    );
}

export interface SignupChangeBroadbandAppointmentDateSelectedEventContext {
  // The appointment date selected by the user
  appointment_date: string;
  // The appointment time slot (AM or PM) selected by the user
  appointment_slot: string;
  // Whether the appointment date/time slot was suggested
  appointment_slot_was_suggested: boolean;
}

// User selects a new broadband appointment date and time slot in the signup journey.
// example usage: tracker.track(trackSignupChangeBroadbandAppointmentDateSelected({prop: 'value'}, {option1: 'value'}));
export function signupChangeBroadbandAppointmentDateSelectedEvent(
  context: SignupChangeBroadbandAppointmentDateSelectedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_change_broadband_appointment_date_selected',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_change_broadband_appointment_date_selected/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupChangeBroadbandAppointmentDateSelected() {
  const { track } = useTracking();

  return (
    context: SignupChangeBroadbandAppointmentDateSelectedEventContext,
    options?: Options
  ) =>
    track(signupChangeBroadbandAppointmentDateSelectedEvent(context, options));
}

export function useTrackSignupChangeBroadbandAppointmentDateSelectedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupChangeBroadbandAppointmentDateSelectedEventContext,
    options?: Options
  ) =>
    trackOnce(
      signupChangeBroadbandAppointmentDateSelectedEvent(context, options)
    );
}

export interface SignupChangeBroadbandAppointmentConfirmedEventContext {
  // The appointment date selected by the user
  new_appointment_date: string;
  // The appointment time slot (AM or PM) selected by the user
  new_appointment_slot: string;
  // Whether the appointment date/time slot was suggested
  appointment_slot_was_suggested: boolean;
}

// User confirms a new broadband appointment date and time slot in the signup journey.
// example usage: tracker.track(trackSignupChangeBroadbandAppointmentConfirmed({prop: 'value'}, {option1: 'value'}));
export function signupChangeBroadbandAppointmentConfirmedEvent(
  context: SignupChangeBroadbandAppointmentConfirmedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_change_broadband_appointment_confirmed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_change_broadband_appointment_confirmed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupChangeBroadbandAppointmentConfirmed() {
  const { track } = useTracking();

  return (
    context: SignupChangeBroadbandAppointmentConfirmedEventContext,
    options?: Options
  ) => track(signupChangeBroadbandAppointmentConfirmedEvent(context, options));
}

export function useTrackSignupChangeBroadbandAppointmentConfirmedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupChangeBroadbandAppointmentConfirmedEventContext,
    options?: Options
  ) =>
    trackOnce(signupChangeBroadbandAppointmentConfirmedEvent(context, options));
}

export interface SignupChangeBroadbandAppointmentComponentViewedEventContext {
  // Number of available appointments returned by the API.
  available_appointments_returned_count: number;
}

// User views the change appointment component (appointment date picker).
// example usage: tracker.track(trackSignupChangeBroadbandAppointmentComponentViewed({prop: 'value'}, {option1: 'value'}));
export function signupChangeBroadbandAppointmentComponentViewedEvent(
  context: SignupChangeBroadbandAppointmentComponentViewedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_change_broadband_appointment_component_viewed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_change_broadband_appointment_component_viewed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupChangeBroadbandAppointmentComponentViewed() {
  const { track } = useTracking();

  return (
    context: SignupChangeBroadbandAppointmentComponentViewedEventContext,
    options?: Options
  ) =>
    track(
      signupChangeBroadbandAppointmentComponentViewedEvent(context, options)
    );
}

export function useTrackSignupChangeBroadbandAppointmentComponentViewedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupChangeBroadbandAppointmentComponentViewedEventContext,
    options?: Options
  ) =>
    trackOnce(
      signupChangeBroadbandAppointmentComponentViewedEvent(context, options)
    );
}

// User submits their selections on the bundle builder page.
// example usage: tracker.track(trackSignupBundleSelected({prop: 'value'}, {option1: 'value'}));
export function signupBundleSelectedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_bundle_selected',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bundle_selected/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupBundleSelected() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBundleSelectedEvent(context, options));
}

export function useTrackSignupBundleSelectedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBundleSelectedEvent(context, options));
}

// User has clicked the "save for later" button in the nav bar, opening theSave and Share form.
// example usage: tracker.track(trackSignupOnequoteSaveClicked({prop: 'value'}, {option1: 'value'}));
export function signupOnequoteSaveClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_onequote_save_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_onequote_save_clicked/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupOnequoteSaveClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupOnequoteSaveClickedEvent(context, options));
}

export function useTrackSignupOnequoteSaveClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupOnequoteSaveClickedEvent(context, options));
}

// User opens the date picker for the expiry date for their home insurance policy (from another provider) in the Thank You page.
// example usage: tracker.track(trackSignupThankyouHomeInsuranceExpiryDatePickerOpened({prop: 'value'}, {option1: 'value'}));
export function signupThankyouHomeInsuranceExpiryDatePickerOpenedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_thankyou_home_insurance_expiry_date_picker_opened',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_thankyou_home_insurance_expiry_date_picker_opened/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupThankyouHomeInsuranceExpiryDatePickerOpened() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(
      signupThankyouHomeInsuranceExpiryDatePickerOpenedEvent(context, options)
    );
}

export function useTrackSignupThankyouHomeInsuranceExpiryDatePickerOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(
      signupThankyouHomeInsuranceExpiryDatePickerOpenedEvent(context, options)
    );
}

// User selected 'Yes' when asked whether to add a joint account holder.
// example usage: tracker.track(trackSignupJointAccountHolderAdded({prop: 'value'}, {option1: 'value'}));
export function signupJointAccountHolderAddedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_joint_account_holder_added',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_joint_account_holder_added/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupJointAccountHolderAdded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupJointAccountHolderAddedEvent(context, options));
}

export function useTrackSignupJointAccountHolderAddedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupJointAccountHolderAddedEvent(context, options));
}

// User has scrolled far enough to have the Thank You page Smart Meter promo header displayed in their viewport.
// example usage: tracker.track(trackSignupThankyouSmartMeterPromoDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupThankyouSmartMeterPromoDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_thankyou_smart_meter_promo_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_thankyou_smart_meter_promo_displayed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupThankyouSmartMeterPromoDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupThankyouSmartMeterPromoDisplayedEvent(context, options));
}

export function useTrackSignupThankyouSmartMeterPromoDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupThankyouSmartMeterPromoDisplayedEvent(context, options));
}

// User has submitted their order/application for signing up as a customer.
// example usage: tracker.track(trackSignupApplicationSubmitted({prop: 'value'}, {option1: 'value'}));
export function signupApplicationSubmittedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_application_submitted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_application_submitted/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupApplicationSubmitted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupApplicationSubmittedEvent(context, options));
}

export function useTrackSignupApplicationSubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupApplicationSubmittedEvent(context, options));
}

// The smart meter upgrade promo banner has been shown to a user during the signup journey.
// example usage: tracker.track(trackSignupEnergySmartMeterPromoDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupEnergySmartMeterPromoDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_energy_smart_meter_promo_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_energy_smart_meter_promo_displayed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupEnergySmartMeterPromoDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupEnergySmartMeterPromoDisplayedEvent(context, options));
}

export function useTrackSignupEnergySmartMeterPromoDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupEnergySmartMeterPromoDisplayedEvent(context, options));
}

// User selected 'Yes' when asked whether to add an additional cardholder to the account's Cashback Card.
// example usage: tracker.track(trackSignupCbcHolderAdded({prop: 'value'}, {option1: 'value'}));
export function signupCbcHolderAddedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_holder_added',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_holder_added/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupCbcHolderAdded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCbcHolderAddedEvent(context, options));
}

export function useTrackSignupCbcHolderAddedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCbcHolderAddedEvent(context, options));
}

export interface SignupBroadbandTariffsDisplayedEventContext {
  // List of tariffs displayed to the user
  tariffs_displayed: string[];
}

// When the user enters broadband page, what tariffs are displayed.
// example usage: tracker.track(trackSignupBroadbandTariffsDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupBroadbandTariffsDisplayedEvent(
  context: SignupBroadbandTariffsDisplayedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_broadband_tariffs_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_broadband_tariffs_displayed/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupBroadbandTariffsDisplayed() {
  const { track } = useTracking();

  return (
    context: SignupBroadbandTariffsDisplayedEventContext,
    options?: Options
  ) => track(signupBroadbandTariffsDisplayedEvent(context, options));
}

export function useTrackSignupBroadbandTariffsDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupBroadbandTariffsDisplayedEventContext,
    options?: Options
  ) => trackOnce(signupBroadbandTariffsDisplayedEvent(context, options));
}

export interface SignupBroadbandFaqExpandedEventContext {
  // The question that was expanded
  question: string;
}

// User has expanded a question from the signup journey broadband FAQs.
// example usage: tracker.track(trackSignupBroadbandFaqExpanded({prop: 'value'}, {option1: 'value'}));
export function signupBroadbandFaqExpandedEvent(
  context: SignupBroadbandFaqExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_broadband_faq_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_broadband_faq_expanded/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupBroadbandFaqExpanded() {
  const { track } = useTracking();

  return (context: SignupBroadbandFaqExpandedEventContext, options?: Options) =>
    track(signupBroadbandFaqExpandedEvent(context, options));
}

export function useTrackSignupBroadbandFaqExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupBroadbandFaqExpandedEventContext, options?: Options) =>
    trackOnce(signupBroadbandFaqExpandedEvent(context, options));
}

export interface SignupQuoteStartedEventContext {
  // Whether the user has entered their address manually
  property_address_manually_entered: boolean;
}

// User clicks the "Get a quote" button to progress through the signup journey.
// example usage: tracker.track(trackSignupQuoteStarted({prop: 'value'}, {option1: 'value'}));
export function signupQuoteStartedEvent(
  context: SignupQuoteStartedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_quote_started',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_quote_started/jsonschema/1-0-4',
      },
    },
  ];
}

export function useTrackSignupQuoteStarted() {
  const { track } = useTracking();

  return (context: SignupQuoteStartedEventContext, options?: Options) =>
    track(signupQuoteStartedEvent(context, options));
}

export function useTrackSignupQuoteStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupQuoteStartedEventContext, options?: Options) =>
    trackOnce(signupQuoteStartedEvent(context, options));
}

// User has scrolled far enough to have the Thank You page Insurance promo header displayed in their viewport.
// example usage: tracker.track(trackSignupThankyouHomeInsurancePromoDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupThankyouHomeInsurancePromoDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_thankyou_home_insurance_promo_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_thankyou_home_insurance_promo_displayed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupThankyouHomeInsurancePromoDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupThankyouHomeInsurancePromoDisplayedEvent(context, options));
}

export function useTrackSignupThankyouHomeInsurancePromoDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupThankyouHomeInsurancePromoDisplayedEvent(context, options));
}

// User submits the expiry date for their home insurance policy (from another provider) in the Thank You page.
// example usage: tracker.track(trackSignupThankyouHomeInsuranceExpirySubmitted({prop: 'value'}, {option1: 'value'}));
export function signupThankyouHomeInsuranceExpirySubmittedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_thankyou_home_insurance_expiry_submitted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_thankyou_home_insurance_expiry_submitted/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupThankyouHomeInsuranceExpirySubmitted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupThankyouHomeInsuranceExpirySubmittedEvent(context, options));
}

export function useTrackSignupThankyouHomeInsuranceExpirySubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(
      signupThankyouHomeInsuranceExpirySubmittedEvent(context, options)
    );
}

// User selected "Yes" when asked "Did you find out about us from another UW customer or Partner?" during checkout and filled in referral details.
// example usage: tracker.track(trackSignupReferrerAdded({prop: 'value'}, {option1: 'value'}));
export function signupReferrerAddedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_referrer_added',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_referrer_added/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupReferrerAdded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupReferrerAddedEvent(context, options));
}

export function useTrackSignupReferrerAddedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupReferrerAddedEvent(context, options));
}

// User fails to complete the change/book appointment flow, due to an error.
// example usage: tracker.track(trackSignupChangeBroadbandAppointmentFailed({prop: 'value'}, {option1: 'value'}));
export function signupChangeBroadbandAppointmentFailedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_change_broadband_appointment_failed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_change_broadband_appointment_failed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupChangeBroadbandAppointmentFailed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupChangeBroadbandAppointmentFailedEvent(context, options));
}

export function useTrackSignupChangeBroadbandAppointmentFailedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupChangeBroadbandAppointmentFailedEvent(context, options));
}

// User completes the change/book appointment flow successfully.
// example usage: tracker.track(trackSignupChangeBroadbandAppointmentCompleted({prop: 'value'}, {option1: 'value'}));
export function signupChangeBroadbandAppointmentCompletedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_change_broadband_appointment_completed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_change_broadband_appointment_completed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupChangeBroadbandAppointmentCompleted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupChangeBroadbandAppointmentCompletedEvent(context, options));
}

export function useTrackSignupChangeBroadbandAppointmentCompletedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupChangeBroadbandAppointmentCompletedEvent(context, options));
}

// User has scrolled far enough to have the the mobile FAQs displayed in their viewport.
// example usage: tracker.track(trackSignupMobileFaqsDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupMobileFaqsDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_mobile_faqs_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_mobile_faqs_displayed/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupMobileFaqsDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupMobileFaqsDisplayedEvent(context, options));
}

export function useTrackSignupMobileFaqsDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupMobileFaqsDisplayedEvent(context, options));
}

export interface SignupOptimizelyActiveExperimentsEntity {
  // List of active Optimizely experiment allocations for the current user
  experiments?: Record<string, any>[];
}

// Active Optimizely experiment/variation allocations for the current user session.Attached as a context entity to business events to enable correlation ofAB test exposure with user behaviour.
export function signupOptimizelyActiveExperimentsEntity(
  context: SignupOptimizelyActiveExperimentsEntity,
  options?: Options
): EntityPayload<SignupOptimizelyActiveExperimentsEntity> {
  return {
    ...options,
    name: 'signup_optimizely_active_experiments',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.signup/signup_optimizely_active_experiments/jsonschema/1-0-0',
    },
  };
}

// User has added at least one mobile service to their basket.
// example usage: tracker.track(trackSignupMobileAddedToBasket({prop: 'value'}, {option1: 'value'}));
export function signupMobileAddedToBasketEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_mobile_added_to_basket',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_mobile_added_to_basket/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupMobileAddedToBasket() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupMobileAddedToBasketEvent(context, options));
}

export function useTrackSignupMobileAddedToBasketOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupMobileAddedToBasketEvent(context, options));
}

// User adds a Cashback Card (CBC) service to their basket.
// example usage: tracker.track(trackSignupCbcAddedToBasket({prop: 'value'}, {option1: 'value'}));
export function signupCbcAddedToBasketEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_added_to_basket',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_added_to_basket/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupCbcAddedToBasket() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCbcAddedToBasketEvent(context, options));
}

export function useTrackSignupCbcAddedToBasketOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCbcAddedToBasketEvent(context, options));
}

export interface SignupMobileFaqExpandedEventContext {
  // The question that was expanded
  question: string;
}

// User has expanded a question from the signup journey mobile FAQs.
// example usage: tracker.track(trackSignupMobileFaqExpanded({prop: 'value'}, {option1: 'value'}));
export function signupMobileFaqExpandedEvent(
  context: SignupMobileFaqExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_mobile_faq_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_mobile_faq_expanded/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupMobileFaqExpanded() {
  const { track } = useTracking();

  return (context: SignupMobileFaqExpandedEventContext, options?: Options) =>
    track(signupMobileFaqExpandedEvent(context, options));
}

export function useTrackSignupMobileFaqExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupMobileFaqExpandedEventContext, options?: Options) =>
    trackOnce(signupMobileFaqExpandedEvent(context, options));
}

export interface SignupCbcCalcStateEntity {
  // whether the user indicated they wish to include the calculated earnings in their bundle savings
  include_in_bundle: boolean;
  estimated_cashback_value: number;
}

// Cashback calculator entity to include current calculator state
export function signupCbcCalcStateEntity(
  context: SignupCbcCalcStateEntity,
  options?: Options
): EntityPayload<SignupCbcCalcStateEntity> {
  return {
    ...options,
    name: 'signup_cbc_calc_state',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.signup/signup_cbc_calc_state/jsonschema/1-0-0',
    },
  };
}

export interface SignupCbcCalcSavedEventContext {
  // variant of the calculator selected, either Category or Brand
  calc_type: string;
}

// User has initiated a save of the current cashback calculator state, either bysubmitting the CBC page or explicitly setting the include in bundle switch.
// example usage: tracker.track(trackSignupCbcCalcSaved({prop: 'value'}, {option1: 'value'}));
export function signupCbcCalcSavedEvent(
  context: SignupCbcCalcSavedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_calc_saved',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_calc_saved/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcCalcSaved() {
  const { track } = useTracking();

  return (context: SignupCbcCalcSavedEventContext, options?: Options) =>
    track(signupCbcCalcSavedEvent(context, options));
}

export function useTrackSignupCbcCalcSavedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupCbcCalcSavedEventContext, options?: Options) =>
    trackOnce(signupCbcCalcSavedEvent(context, options));
}

// User has interacted with the Cashback calculator, changing a valuewhich has generated an earnings estimate
// example usage: tracker.track(trackSignupCbcCalcInteractedWith({prop: 'value'}, {option1: 'value'}));
export function signupCbcCalcInteractedWithEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_cbc_calc_interacted_with',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_cbc_calc_interacted_with/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackSignupCbcCalcInteractedWith() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupCbcCalcInteractedWithEvent(context, options));
}

export function useTrackSignupCbcCalcInteractedWithOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupCbcCalcInteractedWithEvent(context, options));
}

export interface SignupCompletedEventContext {
  // Account number of the new customer
  account_number: string;
}

// User has completed the sign-up journey to become a customer and hence has seen the Thank You page.
// example usage: tracker.track(trackSignupCompleted({prop: 'value'}, {option1: 'value'}));
export function signupCompletedEvent(
  context: SignupCompletedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_completed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_completed/jsonschema/1-0-6',
      },
    },
  ];
}

export function useTrackSignupCompleted() {
  const { track } = useTracking();

  return (context: SignupCompletedEventContext, options?: Options) =>
    track(signupCompletedEvent(context, options));
}

export function useTrackSignupCompletedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupCompletedEventContext, options?: Options) =>
    trackOnce(signupCompletedEvent(context, options));
}

// Information about the failure to find the current broadband supplier,when exploring One Touch Switch (OTS), is displayed in the Yourbroadband switch section on the /order/confirm page.
// example usage: tracker.track(trackSignupOtsBroadbandNotFoundInfoDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupOtsBroadbandNotFoundInfoDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_ots_broadband_not_found_info_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ots_broadband_not_found_info_displayed/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupOtsBroadbandNotFoundInfoDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupOtsBroadbandNotFoundInfoDisplayedEvent(context, options));
}

export function useTrackSignupOtsBroadbandNotFoundInfoDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupOtsBroadbandNotFoundInfoDisplayedEvent(context, options));
}

// The success modal is displayed on the /order/confirm page after a secondattempt to switch through a One Touch Switch (OTS), with theaccountReference added
// example usage: tracker.track(trackSignupOtsSecondTrySuccessModalDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupOtsSecondTrySuccessModalDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_ots_second_try_success_modal_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ots_second_try_success_modal_displayed/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupOtsSecondTrySuccessModalDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupOtsSecondTrySuccessModalDisplayedEvent(context, options));
}

export function useTrackSignupOtsSecondTrySuccessModalDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupOtsSecondTrySuccessModalDisplayedEvent(context, options));
}

export interface SignupEnergyAddedToBasketEventContext {
  // The chosen fuel type
  fuel_type: string;
  // The chosen energy usage category
  energy_usage_category: string;
  // The chosen energy tariff type
  energy_tariff_type?: string;
  // If the user chose a custom energy usage value, this is the value they entered for electricity in kWh
  custom_electricity_usage_kwh?: number;
  // If the user chose a custom energy usage value, this is the value they entered for gas in kWh
  custom_gas_usage_kwh?: number;
  // Whether the user has an economy 7 meter
  econ_seven_meter?: boolean;
  // Whether the user has a prepayment meter
  prepayment_meter?: boolean;
}

// User has added energy to their basket.
// example usage: tracker.track(trackSignupEnergyAddedToBasket({prop: 'value'}, {option1: 'value'}));
export function signupEnergyAddedToBasketEvent(
  context: SignupEnergyAddedToBasketEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_energy_added_to_basket',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_energy_added_to_basket/jsonschema/1-0-6',
      },
    },
  ];
}

export function useTrackSignupEnergyAddedToBasket() {
  const { track } = useTracking();

  return (context: SignupEnergyAddedToBasketEventContext, options?: Options) =>
    track(signupEnergyAddedToBasketEvent(context, options));
}

export function useTrackSignupEnergyAddedToBasketOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupEnergyAddedToBasketEventContext, options?: Options) =>
    trackOnce(signupEnergyAddedToBasketEvent(context, options));
}

export interface SignupBhcFaqExpandedEventContext {
  // The question that was expanded
  question: string;
}

// User expands a question from the Boiler & Home Cover (BHC) FAQs, duringthe sign-up journey.
// example usage: tracker.track(trackSignupBhcFaqExpanded({prop: 'value'}, {option1: 'value'}));
export function signupBhcFaqExpandedEvent(
  context: SignupBhcFaqExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_bhc_faq_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bhc_faq_expanded/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupBhcFaqExpanded() {
  const { track } = useTracking();

  return (context: SignupBhcFaqExpandedEventContext, options?: Options) =>
    track(signupBhcFaqExpandedEvent(context, options));
}

export function useTrackSignupBhcFaqExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupBhcFaqExpandedEventContext, options?: Options) =>
    trackOnce(signupBhcFaqExpandedEvent(context, options));
}

export interface SignupBhcWhatsCoveredFeatureExpandedEventContext {
  // The feature that is expanded e.g. "Electrics"
  feature: string;
}

// In the Boiler & Home Cover (BHC) sign-up journey, the user expands aspecific feature under the "Covered" tab within the "What's covered?"section
// example usage: tracker.track(trackSignupBhcWhatsCoveredFeatureExpanded({prop: 'value'}, {option1: 'value'}));
export function signupBhcWhatsCoveredFeatureExpandedEvent(
  context: SignupBhcWhatsCoveredFeatureExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_bhc_whats_covered_feature_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bhc_whats_covered_feature_expanded/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupBhcWhatsCoveredFeatureExpanded() {
  const { track } = useTracking();

  return (
    context: SignupBhcWhatsCoveredFeatureExpandedEventContext,
    options?: Options
  ) => track(signupBhcWhatsCoveredFeatureExpandedEvent(context, options));
}

export function useTrackSignupBhcWhatsCoveredFeatureExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: SignupBhcWhatsCoveredFeatureExpandedEventContext,
    options?: Options
  ) => trackOnce(signupBhcWhatsCoveredFeatureExpandedEvent(context, options));
}

export interface SignupBhcDocumentClickedEventContext {
  // The title of the document opened by the user (e.g. IPID, Terms ofBusiness, Policy Wording, etc.)
  document_title: string;
}

// In the Boiler & Home Cover (BHC) sign-up journey, the user clicks alink to one of the policy documents
// example usage: tracker.track(trackSignupBhcDocumentClicked({prop: 'value'}, {option1: 'value'}));
export function signupBhcDocumentClickedEvent(
  context: SignupBhcDocumentClickedEventContext,
  options?: Options
): EventTuple {
  return [
    'signup_bhc_document_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bhc_document_clicked/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupBhcDocumentClicked() {
  const { track } = useTracking();

  return (context: SignupBhcDocumentClickedEventContext, options?: Options) =>
    track(signupBhcDocumentClickedEvent(context, options));
}

export function useTrackSignupBhcDocumentClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: SignupBhcDocumentClickedEventContext, options?: Options) =>
    trackOnce(signupBhcDocumentClickedEvent(context, options));
}

// User adds a Boiler & Home Cover (BHC) service to their basket.
// example usage: tracker.track(trackSignupBhcAddedToBasket({prop: 'value'}, {option1: 'value'}));
export function signupBhcAddedToBasketEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_bhc_added_to_basket',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bhc_added_to_basket/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackSignupBhcAddedToBasket() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBhcAddedToBasketEvent(context, options));
}

export function useTrackSignupBhcAddedToBasketOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBhcAddedToBasketEvent(context, options));
}

// In the Boiler & Home Cover (BHC) sign-up journey, the user has scrolledfar enough to have the BHC FAQs displayed in their viewport.
// example usage: tracker.track(trackSignupBhcFaqsDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupBhcFaqsDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_bhc_faqs_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bhc_faqs_displayed/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupBhcFaqsDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBhcFaqsDisplayedEvent(context, options));
}

export function useTrackSignupBhcFaqsDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBhcFaqsDisplayedEvent(context, options));
}

// In the Boiler & Home Cover (BHC) sign-up journey, the user clicks the tabto display what's "Not covered" (within the "What's covered?" section)
// example usage: tracker.track(trackSignupBhcWhatsNotCoveredClicked({prop: 'value'}, {option1: 'value'}));
export function signupBhcWhatsNotCoveredClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_bhc_whats_not_covered_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_bhc_whats_not_covered_clicked/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackSignupBhcWhatsNotCoveredClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupBhcWhatsNotCoveredClickedEvent(context, options));
}

export function useTrackSignupBhcWhatsNotCoveredClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupBhcWhatsNotCoveredClickedEvent(context, options));
}

export interface SignupCoreServicesEntity {
  // Energy is selected at that moment in the journey
  energy: boolean;
  // Broadband is selected at that moment in the journey
  broadband: boolean;
  // Mobile is selected at that moment in the journey
  mobile: boolean;
  // Insurance is selected at that moment in the journey
  insurance: boolean;
  // Multiple SIMs are in the basket, hence this is a flag of a core service via 2+ SIMs
  multiple_sims_selected: boolean;
  // Cashback Card is selected at that moment in the journey
  cbc_selected: boolean;
  // The combinations of core services selected in the application, as used in executive reporting (supporting core service logic from July 2024 onwards)
  core_service_group_quantity_combinations: string;
  // The number of core services (a sum of the 4 core service flags & the multiple SIMs flag)
  number_of_core_services: number;
  // The number of SIMs in the basket
  number_of_mobile_services: number;
  // Basket subtotal for the Energy service price (pennies) at that moment in the journey
  energy_price?: number;
  // The chosen energy fuel type
  energy_fuel_type?: string;
  // The chosen energy tariff type
  energy_tariff_type?: string;
  // Basket subtotal for the Broadband service price (pennies) at that moment in the journey
  broadband_price?: number;
  // Basket subtotal for the Mobile service price (pennies) at that moment in the journey
  mobile_price?: number;
  // Basket subtotal for the Insurance service price (pennies) at that moment in the journey
  insurance_price?: number;
  // Basket subtotal for the Cashback Card service price (pennies) at that moment in the journey
  cbc_price?: number;
  // Total price of the basket, reflecting all services and add-ons
  total_price?: number;
}

// Current selection of core services in the signup journey
export function signupCoreServicesEntity(
  context: SignupCoreServicesEntity,
  options?: Options
): EntityPayload<SignupCoreServicesEntity> {
  return {
    ...options,
    name: 'signup_core_services',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.signup/signup_core_services/jsonschema/1-0-7',
    },
  };
}

// The success modal is displayed on the /order/confirm page after thefirst attempt to switch through a One Touch Switch (OTS), withoutadding the accountReference page.
// example usage: tracker.track(trackSignupOtsFirstTrySuccessModalDisplayed({prop: 'value'}, {option1: 'value'}));
export function signupOtsFirstTrySuccessModalDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'signup_ots_first_try_success_modal_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.signup/signup_ots_first_try_success_modal_displayed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackSignupOtsFirstTrySuccessModalDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(signupOtsFirstTrySuccessModalDisplayedEvent(context, options));
}

export function useTrackSignupOtsFirstTrySuccessModalDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(signupOtsFirstTrySuccessModalDisplayedEvent(context, options));
}

// An Optimizely 'conversion event' has been tracked. See more detailunder the Track Event method documentation for example in the JavaScriptSDK: https://docs.developers.optimizely.com/feature-experimentation/docs/track-event-javascript
// example usage: tracker.track(trackOptimizelyEventTracked({prop: 'value'}, {option1: 'value'}));
export function optimizelyEventTrackedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'optimizely_event_tracked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.external.optimizely/optimizely_event_tracked/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackOptimizelyEventTracked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(optimizelyEventTrackedEvent(context, options));
}

export function useTrackOptimizelyEventTrackedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(optimizelyEventTrackedEvent(context, options));
}

export interface OptimizelyDecisionCalledMetadataEntity {
  // Unique experiment key defined in the Optimizely console
  experiment_key?: string;
  // Variation key resulting from feature decision
  variation_key?: string;
  // Whether the feature is enabled at all
  enabled?: boolean;
  // User ID currently in use by Optimizely session
  user_id?: string;
}

// Metadata for the optimizely_decision_called event
export function optimizelyDecisionCalledMetadataEntity(
  context: OptimizelyDecisionCalledMetadataEntity,
  options?: Options
): EntityPayload<OptimizelyDecisionCalledMetadataEntity> {
  return {
    ...options,
    name: 'optimizely_decision_called_metadata',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.external.optimizely/optimizely_decision_called_metadata/jsonschema/1-0-3',
    },
  };
}

export interface OptimizelyEventTrackedMetadataEntity {
  // Unique key for the event
  event_key?: string;
  // User ID currently in use by Optimizely session
  user_id?: string;
  // Reserved event tag for revenue, defined in pence/cents
  revenue?: number;
  // Reserved event tag for numerical values, either an INT or a FLOAT are valid
  value?: number;
  // A map of default and custom event properties and their values (Optimizely's reserved tag $opt_event_properties).
  opt_event_properties?: Record<string, any>[];
}

// Metadata for the optimizely_event_tracked event
export function optimizelyEventTrackedMetadataEntity(
  context: OptimizelyEventTrackedMetadataEntity,
  options?: Options
): EntityPayload<OptimizelyEventTrackedMetadataEntity> {
  return {
    ...options,
    name: 'optimizely_event_tracked_metadata',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.external.optimizely/optimizely_event_tracked_metadata/jsonschema/1-0-3',
    },
  };
}

// An Optimizely 'decision event' has been called, defining the experimentvariation for a specific user ID. See more detail in Optimizelydocumentation: https://docs.developers.optimizely.com/full-stack-experimentation/docs/impressions
// example usage: tracker.track(trackOptimizelyDecisionCalled({prop: 'value'}, {option1: 'value'}));
export function optimizelyDecisionCalledEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'optimizely_decision_called',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.external.optimizely/optimizely_decision_called/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackOptimizelyDecisionCalled() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(optimizelyDecisionCalledEvent(context, options));
}

export function useTrackOptimizelyDecisionCalledOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(optimizelyDecisionCalledEvent(context, options));
}

// User has started the in-life journey.
// example usage: tracker.track(trackInLifeQuoteStarted({prop: 'value'}, {option1: 'value'}));
export function inLifeQuoteStartedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_quote_started',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_quote_started/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeQuoteStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeQuoteStartedEvent(context, options));
}

export function useTrackInLifeQuoteStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeQuoteStartedEvent(context, options));
}

// User has completed the in-life journey.
// example usage: tracker.track(trackInLifeQuoteCompleted({prop: 'value'}, {option1: 'value'}));
export function inLifeQuoteCompletedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_quote_completed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_quote_completed/jsonschema/1-0-3',
      },
    },
  ];
}

export function useTrackInLifeQuoteCompleted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeQuoteCompletedEvent(context, options));
}

export function useTrackInLifeQuoteCompletedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeQuoteCompletedEvent(context, options));
}

export interface InLifeCoreServicesEntity {
  // Energy is selected at that moment in the journey
  energy_selected: boolean;
  // Broadband is selected at that moment in the journey
  broadband_selected: boolean;
  // Mobile is selected at that moment in the journey
  mobile_selected: boolean;
  // Insurance is selected at that moment in the journey
  insurance_selected: boolean;
}

// Current selection of core services in the in-life journey
export function inLifeCoreServicesEntity(
  context: InLifeCoreServicesEntity,
  options?: Options
): EntityPayload<InLifeCoreServicesEntity> {
  return {
    ...options,
    name: 'in_life_core_services',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.in-life/in_life_core_services/jsonschema/1-0-0',
    },
  };
}

export interface InLifeQuoteLoadedEventContext {
  // Energy tariff choices included in the quote
  energy_tariff_choices?: Record<string, any>[];
}

// A quote has been loaded in the in-life journey.
// example usage: tracker.track(trackInLifeQuoteLoaded({prop: 'value'}, {option1: 'value'}));
export function inLifeQuoteLoadedEvent(
  context: InLifeQuoteLoadedEventContext,
  options?: Options
): EventTuple {
  return [
    'in_life_quote_loaded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_quote_loaded/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackInLifeQuoteLoaded() {
  const { track } = useTracking();

  return (context: InLifeQuoteLoadedEventContext, options?: Options) =>
    track(inLifeQuoteLoadedEvent(context, options));
}

export function useTrackInLifeQuoteLoadedOnce() {
  const trackOnce = useTrackOnce();

  return (context: InLifeQuoteLoadedEventContext, options?: Options) =>
    trackOnce(inLifeQuoteLoadedEvent(context, options));
}

export interface InLifeEnergyAddedToBasketEventContext {
  // The chosen fuel type
  fuel_type: string;
  // The chosen energy usage category
  energy_usage_category: string;
  // The chosen energy tariff type
  energy_tariff_type?: string;
  // If the customer chose a custom energy usage value, this is the value they entered for electricity in kWh
  custom_electricity_usage_kwh?: number;
  // If the customer chose a custom energy usage value, this is the value they entered for gas in kWh
  custom_gas_usage_kwh?: number;
  // Whether the customer has an economy 7 meter
  has_econ_seven_meter?: boolean;
  // Whether the customer has a prepayment meter
  has_prepayment_meter?: boolean;
}

// Customer has added energy to their basket during the in-life journey.
// example usage: tracker.track(trackInLifeEnergyAddedToBasket({prop: 'value'}, {option1: 'value'}));
export function inLifeEnergyAddedToBasketEvent(
  context: InLifeEnergyAddedToBasketEventContext,
  options?: Options
): EventTuple {
  return [
    'in_life_energy_added_to_basket',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_energy_added_to_basket/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackInLifeEnergyAddedToBasket() {
  const { track } = useTracking();

  return (context: InLifeEnergyAddedToBasketEventContext, options?: Options) =>
    track(inLifeEnergyAddedToBasketEvent(context, options));
}

export function useTrackInLifeEnergyAddedToBasketOnce() {
  const trackOnce = useTrackOnce();

  return (context: InLifeEnergyAddedToBasketEventContext, options?: Options) =>
    trackOnce(inLifeEnergyAddedToBasketEvent(context, options));
}

export interface InLifeEnergyFaqExpandedEventContext {
  // The question that was expanded
  question: string;
}

// Customer has expanded a question from the in-life journey energy FAQs.
// example usage: tracker.track(trackInLifeEnergyFaqExpanded({prop: 'value'}, {option1: 'value'}));
export function inLifeEnergyFaqExpandedEvent(
  context: InLifeEnergyFaqExpandedEventContext,
  options?: Options
): EventTuple {
  return [
    'in_life_energy_faq_expanded',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_energy_faq_expanded/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEnergyFaqExpanded() {
  const { track } = useTracking();

  return (context: InLifeEnergyFaqExpandedEventContext, options?: Options) =>
    track(inLifeEnergyFaqExpandedEvent(context, options));
}

export function useTrackInLifeEnergyFaqExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: InLifeEnergyFaqExpandedEventContext, options?: Options) =>
    trackOnce(inLifeEnergyFaqExpandedEvent(context, options));
}

// The "Give us a quick call" banner has been displayed during the in-lifejourney after the customer confirmed the address we hold for them is incorrect.
// example usage: tracker.track(trackInLifeAddressIncorrectBannerDisplayed({prop: 'value'}, {option1: 'value'}));
export function inLifeAddressIncorrectBannerDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_address_incorrect_banner_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_address_incorrect_banner_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeAddressIncorrectBannerDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeAddressIncorrectBannerDisplayedEvent(context, options));
}

export function useTrackInLifeAddressIncorrectBannerDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeAddressIncorrectBannerDisplayedEvent(context, options));
}

// Customer has scrolled far enough to have the in-life journey energy FAQs header displayed in their viewport.
// example usage: tracker.track(trackInLifeEnergyFaqsHeaderDisplayed({prop: 'value'}, {option1: 'value'}));
export function inLifeEnergyFaqsHeaderDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_energy_faqs_header_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_energy_faqs_header_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEnergyFaqsHeaderDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeEnergyFaqsHeaderDisplayedEvent(context, options));
}

export function useTrackInLifeEnergyFaqsHeaderDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeEnergyFaqsHeaderDisplayedEvent(context, options));
}

// The smart meter upgrade promo banner has been shown to a customer during the in-life journey.
// example usage: tracker.track(trackInLifeEnergySmartMeterPromoDisplayed({prop: 'value'}, {option1: 'value'}));
export function inLifeEnergySmartMeterPromoDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_energy_smart_meter_promo_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_energy_smart_meter_promo_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEnergySmartMeterPromoDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeEnergySmartMeterPromoDisplayedEvent(context, options));
}

export function useTrackInLifeEnergySmartMeterPromoDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeEnergySmartMeterPromoDisplayedEvent(context, options));
}

// Customer clicked "Edit" energy details, changed something and clicked "Save details".
// example usage: tracker.track(trackInLifeEnergyDetailsEdited({prop: 'value'}, {option1: 'value'}));
export function inLifeEnergyDetailsEditedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_energy_details_edited',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_energy_details_edited/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEnergyDetailsEdited() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeEnergyDetailsEditedEvent(context, options));
}

export function useTrackInLifeEnergyDetailsEditedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeEnergyDetailsEditedEvent(context, options));
}

export interface InLifeAddressConfirmedEventContext {
  // Whether the customer confirmed the address we hold is correct or incorrect
  address_confirmation: string;
}

// Customer confirmed whether the address we hold for them is correct or incorrectduring the in-life journey.
// example usage: tracker.track(trackInLifeAddressConfirmed({prop: 'value'}, {option1: 'value'}));
export function inLifeAddressConfirmedEvent(
  context: InLifeAddressConfirmedEventContext,
  options?: Options
): EventTuple {
  return [
    'in_life_address_confirmed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_address_confirmed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeAddressConfirmed() {
  const { track } = useTracking();

  return (context: InLifeAddressConfirmedEventContext, options?: Options) =>
    track(inLifeAddressConfirmedEvent(context, options));
}

export function useTrackInLifeAddressConfirmedOnce() {
  const trackOnce = useTrackOnce();

  return (context: InLifeAddressConfirmedEventContext, options?: Options) =>
    trackOnce(inLifeAddressConfirmedEvent(context, options));
}

// Customer has submitted their order for adding a service in the in-life journey.
// example usage: tracker.track(trackInLifeOrderSubmitted({prop: 'value'}, {option1: 'value'}));
export function inLifeOrderSubmittedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_order_submitted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_order_submitted/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeOrderSubmitted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeOrderSubmittedEvent(context, options));
}

export function useTrackInLifeOrderSubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeOrderSubmittedEvent(context, options));
}

// The in-life "Add energy" tile has been displayed to the customer.
// example usage: tracker.track(trackInLifeAddEnergyTileDisplayed({prop: 'value'}, {option1: 'value'}));
export function inLifeAddEnergyTileDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_add_energy_tile_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_add_energy_tile_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeAddEnergyTileDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeAddEnergyTileDisplayedEvent(context, options));
}

export function useTrackInLifeAddEnergyTileDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeAddEnergyTileDisplayedEvent(context, options));
}

// In the in-life energy journey, the customer has scrolled far enoughto have the EV banner displayed in their viewport.
// example usage: tracker.track(trackInLifeEvBannerDisplayed({prop: 'value'}, {option1: 'value'}));
export function inLifeEvBannerDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_ev_banner_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_ev_banner_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEvBannerDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeEvBannerDisplayedEvent(context, options));
}

export function useTrackInLifeEvBannerDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeEvBannerDisplayedEvent(context, options));
}

export interface InLifeHomeownerStatusSubmittedEventContext {
  // The homeowner status the customer selected
  homeowner_status: string;
}

// Customer submitted whether they own or rent their property during the in-life journey.
// example usage: tracker.track(trackInLifeHomeownerStatusSubmitted({prop: 'value'}, {option1: 'value'}));
export function inLifeHomeownerStatusSubmittedEvent(
  context: InLifeHomeownerStatusSubmittedEventContext,
  options?: Options
): EventTuple {
  return [
    'in_life_homeowner_status_submitted',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_homeowner_status_submitted/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeHomeownerStatusSubmitted() {
  const { track } = useTracking();

  return (
    context: InLifeHomeownerStatusSubmittedEventContext,
    options?: Options
  ) => track(inLifeHomeownerStatusSubmittedEvent(context, options));
}

export function useTrackInLifeHomeownerStatusSubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: InLifeHomeownerStatusSubmittedEventContext,
    options?: Options
  ) => trackOnce(inLifeHomeownerStatusSubmittedEvent(context, options));
}

// In the in-life energy journey, the customer clicks alink to see full EV tariff details.
// example usage: tracker.track(trackInLifeEvLinkClicked({prop: 'value'}, {option1: 'value'}));
export function inLifeEvLinkClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_ev_link_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_ev_link_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEvLinkClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeEvLinkClickedEvent(context, options));
}

export function useTrackInLifeEvLinkClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeEvLinkClickedEvent(context, options));
}

// A banner informing the customer that energy tariffs are currently unavailable has beendisplayed during the in-life journey.
// example usage: tracker.track(trackInLifeEnergyTariffsUnavailableBannerDisplayed({prop: 'value'}, {option1: 'value'}));
export function inLifeEnergyTariffsUnavailableBannerDisplayedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_energy_tariffs_unavailable_banner_displayed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_energy_tariffs_unavailable_banner_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEnergyTariffsUnavailableBannerDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeEnergyTariffsUnavailableBannerDisplayedEvent(context, options));
}

export function useTrackInLifeEnergyTariffsUnavailableBannerDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(
      inLifeEnergyTariffsUnavailableBannerDisplayedEvent(context, options)
    );
}

// This event triggers when the customer's homeowner status is unknown in the in-life quoting journey, at the time they try to get a quote in the Add Energy section. Nothing is displayed to the user when this event triggers (this is only for internal measurement).
// example usage: tracker.track(trackInLifeHomeownerStatusIsUnknown({prop: 'value'}, {option1: 'value'}));
export function inLifeHomeownerStatusIsUnknownEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_homeowner_status_is_unknown',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_homeowner_status_is_unknown/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeHomeownerStatusIsUnknown() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeHomeownerStatusIsUnknownEvent(context, options));
}

export function useTrackInLifeHomeownerStatusIsUnknownOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeHomeownerStatusIsUnknownEvent(context, options));
}

export interface InLifeQuotingFieldsEntity {
  // The customer account number
  account_number: string;
  // The unique identifier for the basket
  basket_id?: string;
}

// In-life journey meta entity. Data points applied to every event.
export function inLifeQuotingFieldsEntity(
  context: InLifeQuotingFieldsEntity,
  options?: Options
): EntityPayload<InLifeQuotingFieldsEntity> {
  return {
    ...options,
    name: 'in_life_quoting_fields',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.quoting.in-life/in_life_quoting_fields/jsonschema/1-0-1',
    },
  };
}

// The "Get a quote" button has been clicked.
// example usage: tracker.track(trackInLifeEnergyGetAQuoteButtonClicked({prop: 'value'}, {option1: 'value'}));
export function inLifeEnergyGetAQuoteButtonClickedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_energy_get_a_quote_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_energy_get_a_quote_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackInLifeEnergyGetAQuoteButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeEnergyGetAQuoteButtonClickedEvent(context, options));
}

export function useTrackInLifeEnergyGetAQuoteButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeEnergyGetAQuoteButtonClickedEvent(context, options));
}

// The in-life "Add energy" page has been displayed to the customer.
// example usage: tracker.track(trackInLifeAddEnergyPageViewed({prop: 'value'}, {option1: 'value'}));
export function inLifeAddEnergyPageViewedEvent(
  context?: Context,
  options?: Options
): EventTuple {
  return [
    'in_life_add_energy_page_viewed',
    context,
    {
      ...options,
      snowplow: {
        ...options?.snowplow,
        schema:
          'iglu:uk.co.uw.quoting.in-life/in_life_add_energy_page_viewed/jsonschema/1-0-1',
      },
    },
  ];
}

export function useTrackInLifeAddEnergyPageViewed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inLifeAddEnergyPageViewedEvent(context, options));
}

export function useTrackInLifeAddEnergyPageViewedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inLifeAddEnergyPageViewedEvent(context, options));
}

export interface PersonEntity {
  // Person id
  id: string;
  // Email of the person
  email?: string;
}

// Person entity
export function personEntity(
  context: PersonEntity,
  options?: Options
): EntityPayload<PersonEntity> {
  return {
    ...options,
    name: 'person',
    data: context,
    snowplow: { schema: 'iglu:uk.co.uw/person/jsonschema/1-1-2' },
  };
}
